/**
 * Каркас кабинета: боковая навигация и «приклеенная» шапка раздела.
 *
 * Сверху сайдбара — бренд, от имени которого ты действуешь. Это то же место и
 * та же роль, что у переключателя рабочего пространства в Linear: сначала
 * отвечаем на вопрос «за кого я сейчас», потом на «что я делаю».
 */
import { Avatar, BrandMark, Button, Icon, ThemeToggle, type IconName } from '@allswipes/ui';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { canPublish, useConsole } from '../session';
import { SiteFooter } from './SiteFooter';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end: boolean;
  /** Пункт только для тех, кто ведёт акции: владельца и менеджера. */
  publishersOnly?: boolean;
}

// Меню по роли.
//
// Выдающий призы видит только выдачу: остальные разделы ему всё равно
// отвечают отказом, а пункт меню, который при нажатии говорит «нельзя», —
// это обещание, которого интерфейс не держит. Права решает сервер, здесь
// лишь не показывается то, чего человек не может.
const GROUPS: Array<{ title?: string; items: NavItem[]; publishersOnly?: boolean }> = [
  {
    publishersOnly: true,
    items: [
      { to: '/', label: 'Дашборд', icon: 'dashboard', end: true },
      { to: '/campaigns', label: 'Акции', icon: 'list', end: false },
      { to: '/create', label: 'Создать акцию', icon: 'plus', end: false },
    ],
  },
  {
    title: 'Операции',
    items: [
      { to: '/claims', label: 'Выдача призов', icon: 'gift', end: false },
      { to: '/reviews', label: 'Отзывы', icon: 'star', end: false, publishersOnly: true },
      { to: '/audit', label: 'Аудит-журнал', icon: 'scroll', end: false, publishersOnly: true },
    ],
  },
  {
    title: 'Настройки',
    publishersOnly: true,
    items: [
      { to: '/brand', label: 'Бренд', icon: 'building', end: false },
      { to: '/team', label: 'Команда', icon: 'user', end: false },
    ],
  },
];

export function Layout({
  children,
  title,
  subtitle,
  action,
}: {
  children: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  const { me, signOut } = useConsole();
  const mayPublish = canPublish(me?.member.role);
  const navigate = useNavigate();
  const pending = me?.stats.pending_claims ?? 0;

  return (
    <div className="console">
      <aside className="side">
        {me && (
          <NavLink to="/brand" className="ws" title="Профиль бренда">
            <BrandMark name={me.brand.name} logoUrl={me.brand.logo_url} size={22} />
            <span className="nm">{me.brand.name}</span>
            {/* Тариф рядом с названием: от него зависит, что в кабинете видно. */}
            {me.brand.plan === 'pro' && <span className="plan">Про</span>}
            <Icon name="chevronDown" size={13} />
          </NavLink>
        )}

        {GROUPS.filter((g) => !g.publishersOnly || mayPublish).map((group, gi) => (
          <div key={group.title ?? gi}>
            {group.title && <div className="grp">{group.title}</div>}
            {group.items
              .filter((item) => !item.publishersOnly || mayPublish)
              .map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}>
                <Icon name={item.icon} size={15} />
                {item.label}
                {item.to === '/claims' && pending > 0 && <span className="cnt">{pending}</span>}
              </NavLink>
            ))}
          </div>
        ))}

        {me && (
          <div className="foot">
            <div className="row" style={{ gap: 7, padding: '4px 6px 8px' }}>
              <Avatar name={me.member.email} size={20} />
              <span
                className="faint"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={`${me.member.email} · ${me.member.role}`}
              >
                {me.member.email}
              </span>
            </div>
            <div className="row">
              <ThemeToggle />
              <span className="spacer" />
              <Button
                variant="ghost"
                size="sm"
                icon="logout"
                title="Выйти"
                onClick={() => {
                  signOut();
                  navigate('/login');
                }}
              />
            </div>
          </div>
        )}
      </aside>

      <div className="content">
        <header className="chead">
          <div style={{ minWidth: 0 }}>
            <h1 className="h1">{title}</h1>
            {subtitle && <p className="sub">{subtitle}</p>}
          </div>
          <span className="spacer" />
          {action}
        </header>
        <div className="cbody">
          {children}
          <SiteFooter />
        </div>
      </div>
    </div>
  );
}
